import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { ElasticCache, ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { VSHistoryData, VSHistoryResult } from "game/DragonsVsPandasGameModel";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { Container, Text } from "pixi.js";
import { minimumHeightSpecSelector, orientationSpecSelector } from "utils/Responsive";

enum ResultType {
    WIN = `WIN`,
    LOSS = `LOSS`,
    DRAW = `DRAW`
}

type VSResults = {
    dragons: VSHistoryResult[],
    pandas: VSHistoryResult[]
}

class HistoryValue {
    text: Text;
    valueBounds: BoundedContainer;
    targetPreviousGames: number;

    constructor(services: GameServices, spec: Record<string, any>, textSpec: any, targetPreviousGames: number) {
        const {factory} = services;

        this.targetPreviousGames = targetPreviousGames;
        this.valueBounds = new BoundedContainer({width: spec.valueBounds.width, height: spec.valueBounds.height / this.targetPreviousGames});

        const fontSizeMultiplier = 20 / this.targetPreviousGames;
        if (fontSizeMultiplier < 1) {
            textSpec.style.fontSize *= fontSizeMultiplier;
        }

        this.text = factory.createText(``, textSpec, this.valueBounds);
    }

    getBoundedScale(): number {

        return this.valueBounds.scale.x;
    }

    setBoundedScale(value: number): void {

        this.valueBounds.scale.set(value);
    }

    setParent(container: Container): void {
        container.addChild(this.valueBounds);
    }

    setText(message: string): void {
        this.text.text = message;
        this.valueBounds.update();
    }

    setFontSize(size: number): void {
        this.text.style.fontSize = size;
        this.valueBounds.update();
    }

    setVisible(visible: boolean): void {
        this.text.visible = visible;
        this.valueBounds.visible = visible;
    }

    setY(position: number): void {
        this.valueBounds.y = position;
    }

    setValueBounds(width: number, height: number): void {
        this.valueBounds.maxSize.width = width;
        this.valueBounds.maxSize.height = height / this.targetPreviousGames;
    }
}

export class VSHistory extends View {
    private readonly header: Text | AdvancedText;
    private headerContainer!: BoundedContainer;
    private dragonsResultsOne!: Container;
    private pandasResultsOne!: Container;
    private dragonsResultsTwo!: Container;
    private pandasResultsTwo!: Container;
    private resultsCache!: {dragonWin: ElasticCache<HistoryValue>, dragonLoss: ElasticCache<HistoryValue>,
                            pandaWin: ElasticCache<HistoryValue>, pandaLoss: ElasticCache<HistoryValue>};
    private activeResults = {dragonWin: [] as ExternalItem<HistoryValue>[], dragonLoss: [] as ExternalItem<HistoryValue>[],
                             pandaWin: [] as ExternalItem<HistoryValue>[], pandaLoss: [] as ExternalItem<HistoryValue>[]};
    private targetPreviousGames!: number;
    private portraitMode: boolean = false;
    private vsResults: VSResults | undefined;

    constructor(protected override readonly services: GameServices, protected readonly spec: Record<string, any>) {
        super(services);

        const {factory} = this.services;

        this.header = factory.createText(``, spec.vsHistoryHeader, this);
        this.headerContainer = ImplementBoundedContainer(this.header, this.spec.landscape.historyHeader.maxSize);

        this.targetPreviousGames = spec.vsHistoryTargetGames;

        this.setupResults(spec);
    }

    set(vsHistory?: VSHistoryData): void {

        this.vsResults = vsHistory?.vsResults;

        this.updateHeaderText();
        this.populateResultsContainers();
    }

    updateHeaderText(): void {

        let previousGames: number | undefined = undefined;
        if (this.vsResults) {

            previousGames = this.vsResults.dragons.length;
        }

        const {resources} = this.services;

        let headerMessage;

        if (previousGames === undefined || previousGames === 0) {
            headerMessage = this.portraitMode ? resources.getMessage(`vsHistoryNoPreviousGamesPortrait`) : resources.getMessage(`vsHistoryNoPreviousGames`);

        } else if (previousGames === 1) {
            headerMessage = this.portraitMode ? resources.getMessage(`vsHistoryOnePreviousGamePortrait`) : resources.getMessage(`vsHistoryOnePreviousGame`);

        } else if (previousGames <= this.targetPreviousGames) {
            headerMessage = resources.getMessage(this.portraitMode ? `vsHistoryXPreviousGamesPortrait` : `vsHistoryXPreviousGames`, {"games": `${previousGames}`});
        } else {
            headerMessage = resources.getMessage(this.portraitMode ? `vsHistoryXPreviousGamesPortrait` : `vsHistoryXPreviousGames`, {"games": `${this.targetPreviousGames}`});
        }

        this.header.text = headerMessage;

        this.headerContainer.update();
    }

    setLandscape(): void {
        this.dragonsResultsTwo.visible = false;
        this.pandasResultsTwo.visible = false;

        this.services.factory.applySpec(this.headerContainer, this.spec.landscape.historyHeader);
        this.headerContainer.maxSize = this.spec.landscape.historyHeader.maxSize;

        this.services.factory.applySpec(this.dragonsResultsOne, this.spec.landscape.historyResultsDragonsOne);
        this.services.factory.applySpec(this.pandasResultsOne, this.spec.landscape.historyResultsPandasOne);

        if (this.portraitMode) {

            this.portraitMode = false;

            this.freeCachedResults();

            this.updateHeaderText();
            this.populateResultsContainers();
        }
    }

    setPortrait(): void {
        this.dragonsResultsTwo.visible = true;
        this.pandasResultsTwo.visible = true;

        const headerSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyHeader);
        this.services.factory.applySpec(this.headerContainer, orientationSpecSelector(this.services, this.spec, headerSpec));
        this.headerContainer.maxSize = this.spec.portrait.historyHeaderMaxSize;

        const dragonsOneSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyResultsDragonsOne);
        this.services.factory.applySpec(this.dragonsResultsOne, orientationSpecSelector(this.services, this.spec, dragonsOneSpec));
        const pandasOneSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyResultsPandasOne);
        this.services.factory.applySpec(this.pandasResultsOne, orientationSpecSelector(this.services, this.spec, pandasOneSpec));

        const dragonsTwoSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyResultsDragonsTwo);
        this.services.factory.applySpec(this.dragonsResultsTwo, orientationSpecSelector(this.services, this.spec, dragonsTwoSpec));
        const pandasTwoSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyResultsPandasTwo);
        this.services.factory.applySpec(this.pandasResultsTwo, orientationSpecSelector(this.services, this.spec, pandasTwoSpec));

        if (!this.portraitMode) {

            this.portraitMode = true;

            this.freeCachedResults();

            this.updateHeaderText();
            this.populateResultsContainers();
        }
    }

    override reset(): void {
        this.freeCachedResults();
    }

    private setupResults(spec: Record<string, any>): void {

        const {factory} = this.services;

        this.dragonsResultsOne = factory.createContainer(spec.vsHistoryDragonResults, this);
        this.pandasResultsOne = factory.createContainer(spec.vsHistoryPandaResults, this);

        this.dragonsResultsTwo = factory.createContainer(spec.vsHistoryDragonResults, this);
        this.pandasResultsTwo = factory.createContainer(spec.vsHistoryPandaResults, this);

        const getCreator = (historySpec: any) => () => new HistoryValue(this.services, spec, JSON.parse(JSON.stringify(historySpec)), this.targetPreviousGames);
        const get = (item: HistoryValue) => item.setVisible(true);
        const free = (item: HistoryValue) => item.setVisible(false);

        const dragonWin = new ElasticCache<HistoryValue>(getCreator(spec.vsHistoryDragonWin), get, free);
        const dragonLoss = new ElasticCache<HistoryValue>(getCreator(spec.vsHistoryDragonLoss), get, free);
        const pandaWin = new ElasticCache<HistoryValue>(getCreator(spec.vsHistoryPandaWin), get, free);
        const pandaLoss = new ElasticCache<HistoryValue>(getCreator(spec.vsHistoryPandaLoss), get, free);

        this.resultsCache = {
            dragonWin,
            dragonLoss,
            pandaWin,
            pandaLoss
        };
    }

    private populateResultsContainers(): void {

        this.dragonsResultsOne.removeChildren();
        this.pandasResultsOne.removeChildren();

        this.dragonsResultsTwo.removeChildren();
        this.pandasResultsTwo.removeChildren();

        if (this.vsResults) {

            if (this.portraitMode) {

                this.populateResultsContainer(this.dragonsResultsOne, this.vsResults.dragons, `Dragons`, 0);
                this.populateResultsContainer(this.pandasResultsOne, this.vsResults.pandas, `Pandas`, 0);

                this.populateResultsContainer(this.dragonsResultsTwo, this.vsResults.dragons, `Dragons`, 1);
                this.populateResultsContainer(this.pandasResultsTwo, this.vsResults.pandas, `Pandas`, 1);
            } else {

                this.populateResultsContainer(this.dragonsResultsOne, this.vsResults.dragons, `Dragons`);
                this.populateResultsContainer(this.pandasResultsOne, this.vsResults.pandas, `Pandas`);
            }

            this.updateHistoryBoundedScales();
        }
    }

    private populateResultsContainer(container: Container, results: VSHistoryResult[], label: string, resultListInstance: number = 0): void {

        let text;

        let i;
        if (resultListInstance === 1 && this.portraitMode) {

            if (results.length <= this.targetPreviousGames / 2) return;

            i = results.length - (this.targetPreviousGames / 2 + 1);
        } else {

            i = results.length - 1;
        }

        const limit = resultListInstance === 0 && this.portraitMode ? this.targetPreviousGames / 2 : this.targetPreviousGames;
        const valueBounds = this.portraitMode ? this.spec.portrait.historyResultsValues.valueBounds : this.spec.landscape.historyResultsValues.valueBounds;
        const fontSize = this.portraitMode ? this.spec.portrait.historyResultsValues.fontSize : this.spec.landscape.historyResultsValues.fontSize;
        const resultVerticalSpacing = this.portraitMode ? this.spec.vsHistoryResultVerticalSpacing.portrait / this.targetPreviousGames
                                                        : this.spec.vsHistoryResultVerticalSpacing.landscape / this.targetPreviousGames;

        for (i; i >= 0 && i >= results.length - limit; i--) {

            const item = results[i];

            if (label === `Dragons`) {
                text = (item.type === ResultType.WIN) ? this.resultsCache.dragonWin.get() : this.resultsCache.dragonLoss.get();
            } else {
                text = (item.type === ResultType.WIN) ? this.resultsCache.pandaWin.get() : this.resultsCache.pandaLoss.get();
            }

            text.reference.setValueBounds(valueBounds.width, valueBounds.height);
            text.reference.setText(`` + this.services.currency.formatCoins(item.value, {group: true}));
            text.reference.setFontSize(fontSize);
            text.reference.setY(container.children.length * resultVerticalSpacing);
            text.reference.setParent(container);

            if (label === `Dragons`) {
                if (item.type === ResultType.WIN) {
                    this.activeResults.dragonWin.push(text);
                } else {
                    this.activeResults.dragonLoss.push(text);
                }
            } else {
                if (item.type === ResultType.WIN) {
                    this.activeResults.pandaWin.push(text);
                } else {
                    this.activeResults.pandaLoss.push(text);
                }
            }
        }
    }

    private updateHistoryBoundedScales(): void {

        const getBoundedScale = (item: ExternalItem<HistoryValue>) => item.reference.getBoundedScale();
        const smallestScale = Math.min(
            ...this.activeResults.dragonWin.map(getBoundedScale),
            ...this.activeResults.dragonLoss.map(getBoundedScale),
            ...this.activeResults.pandaWin.map(getBoundedScale),
            ...this.activeResults.pandaLoss.map(getBoundedScale)
        );

        const setBoundedScale = (item: ExternalItem<HistoryValue>) => item.reference.setBoundedScale(smallestScale);
        this.activeResults.dragonWin.forEach(setBoundedScale);
        this.activeResults.dragonLoss.forEach(setBoundedScale);
        this.activeResults.pandaWin.forEach(setBoundedScale);
        this.activeResults.pandaLoss.forEach(setBoundedScale);
    }

    private freeCachedResults(): void {

        for (const item of this.activeResults.dragonWin) this.resultsCache.dragonWin.free(item);
        for (const item of this.activeResults.dragonLoss) this.resultsCache.dragonLoss.free(item);

        for (const item of this.activeResults.pandaWin) this.resultsCache.pandaWin.free(item);
        for (const item of this.activeResults.pandaLoss) this.resultsCache.pandaLoss.free(item);

        this.activeResults.dragonWin = [];
        this.activeResults.dragonLoss = [];

        this.activeResults.pandaWin = [];
        this.activeResults.pandaLoss = [];
    }
}